import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { logPlatformGating } from "@/utils/platformGatingLog";
import { useApplications } from "@/hooks/useApplications";
import { useAppConfigsForApp } from "@/hooks/useAppConfigurations";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SectionCard } from "@/components/ui/section-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Copy, Trash2, Upload, CheckCircle, XCircle, Loader2, Shield, Plus, Info, RefreshCw } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { AppConfigList } from "@/components/applications/config/AppConfigList";
import { AppConfigDialog } from "@/components/applications/config/AppConfigDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { PrivacyPermissionsCard } from "@/components/applications/pppc/PrivacyPermissionsCard";
import { PrivacyPermissionsDialog } from "@/components/applications/pppc/PrivacyPermissionsDialog";
import { PppcActivityTimeline } from "@/components/applications/pppc/PppcActivityTimeline";
import { usePppcPolicy } from "@/hooks/usePppcPolicy";
import { toast } from "sonner";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";
import { Platform } from "@/types/profile";
import { isPlatform } from "@/utils/platformUtils";
import { validateBundleId } from "@/utils/bundleIdValidation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pppcQueryKey } from "@/hooks/usePppcPolicy";
import * as privateAppsApi from "@/services/privateAppsApi";
import type { ApplicationDTO } from "@/types/api";
import { DetailPageLayout } from "@/components/ui/detail-page-layout";
import { Skeleton } from "@/components/ui/skeleton";

// Application IDs in this app are server-issued UUIDs or short alphanumeric
// slugs. Anything outside [A-Za-z0-9_-] (length 1..128) is treated as
// malformed so we can show a precise reason on the fallback card instead of
// firing a doomed network request.
const APP_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

type AppIdProblem =
  | { kind: "missing" }
  | { kind: "blank"; raw: string }
  | { kind: "too-long"; raw: string; length: number }
  | { kind: "invalid-characters"; raw: string };

function diagnoseAppId(raw: string | undefined): AppIdProblem | null {
  if (raw === undefined) return { kind: "missing" };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: "blank", raw };
  if (trimmed.length > 128) {
    return { kind: "too-long", raw: trimmed, length: trimmed.length };
  }
  if (!APP_ID_PATTERN.test(trimmed)) {
    return { kind: "invalid-characters", raw: trimmed };
  }
  return null;
}

import { formatAppSize } from "@/utils/appSize";


export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { removeApplication } = useApplications();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAppConfigDialog, setShowAppConfigDialog] = useState(false);
  const [showPppcDialog, setShowPppcDialog] = useState(false);
  const [confirmConfigureKind, setConfirmConfigureKind] = useState<null | "pppc">(null);

  const queryClient = useQueryClient();
  // Diagnose the route id BEFORE issuing any network request so a malformed
  // value never reaches the API. Malformed ids redirect to /applications
  // instead of rendering the inline fallback card.
  const idProblem = diagnoseAppId(id);
  const hasNotifiedInvalidId = useRef(false);

  useEffect(() => {
    if (!idProblem) {
      hasNotifiedInvalidId.current = false;
      return;
    }
    if (hasNotifiedInvalidId.current) return;
    hasNotifiedInvalidId.current = true;

    // Surface a precise reason via toast so the user understands why the URL
    // didn't open. The redirect itself is handled below via <Navigate />.
    let reason: string;
    switch (idProblem.kind) {
      case "missing":
        reason = "The application link was missing an identifier.";
        break;
      case "blank":
        reason = "The application link contained an empty identifier.";
        break;
      case "too-long":
        reason = `The application identifier was too long (${idProblem.length} characters, max 128).`;
        break;
      case "invalid-characters":
        reason = `"${idProblem.raw}" isn't a valid application identifier.`;
        break;
    }
    toast.error(reason, { description: "Returning to the applications list." });
  }, [idProblem]);

  const { data: appData, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['application-detail', id],
    queryFn: () => privateAppsApi.getPrivateApp(id!),
    enabled: !!id && idProblem === null,
  });

  if (idProblem) {
    return <Navigate to="/applications" replace />;
  }

  // Re-fetch both the application detail and any PPPC policy keyed off the
  // app's (possibly stale) bundleId. Safe to call from the fallback card,
  // since invalidating an absent key is a no-op.
  const handleRetry = () => {
    void refetch();
    const staleBundleId =
      typeof appData?.bundleId === "string" ? appData.bundleId : undefined;
    queryClient.invalidateQueries({ queryKey: pppcQueryKey(staleBundleId) });
  };

  const app = (appData ?? null) as ApplicationDTO | null;
  const isMacOs = isPlatform(app?.platform, "macos");
  // Validate bundleId before issuing the PPPC fetch — PPPC policies are
  // keyed by bundle id, so a malformed value would just produce a noisy
  // backend error. We surface a friendly inline warning instead.
  const bundleIdValidation = validateBundleId(app?.bundleId);
  const pppcBundleId = isMacOs && bundleIdValidation.valid
    ? bundleIdValidation.normalized
    : undefined;
  const { data: pppcData } = usePppcPolicy(pppcBundleId);

  if (isLoading) {
    // Skeleton mirrors the redesigned layout: compact hero with inline
    // metadata + tag chips, then a 2-column grid with a single Overview card
    // (definition list) on the left and a slim sidebar on the right.
    return (
      <div className="px-6 py-6 space-y-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading application details…</span>

        {/* Back link / breadcrumb */}
        <Skeleton className="h-4 w-40" />

        {/* Hero header: icon + title + compact meta row + tag chips */}
        <div className="flex items-start gap-4">
          <Skeleton className="h-16 w-16 rounded-xl shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-7 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex items-center gap-2 pt-1">
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-5 w-14" />
              <Skeleton className="h-5 w-10" />
              <Skeleton className="h-3 w-32 ml-2" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-9 w-28 shrink-0" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Overview definition list */}
          <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <Skeleton className="h-5 w-24" />
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-3.5 w-28 shrink-0" />
                      <Skeleton className="h-3.5 flex-1" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right column: compact sidebar — Configurations + PPPC placeholders */}
            <div className="space-y-6">
              <Card>
                <CardHeader className="space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-3 w-48" />
                </CardHeader>
                <CardContent className="space-y-2">
                  <Skeleton className="h-[68px] w-full rounded-md" />
                  <Skeleton className="h-[68px] w-full rounded-md" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-3 w-56" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-full" />
                  <div className="border-t pt-3 space-y-2">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
      </div>
    );
  }

  // Safe fallback: render an explanatory error UI whenever we don't have a
  // valid `app` object. (Malformed ids are redirected above via <Navigate />,
  // so we only need to handle network errors and not-found here.) This MUST
  // run before any code reads fields like `app.platform` or `app.bundleId`
  // so we never dereference null/undefined.
  if (error || !app) {
    let title: string;
    let message: ReactNode;
    let canRetry = true;

    if (error) {
      title = "Couldn't load application";
      message =
        (error as Error).message ||
        "Something went wrong while loading this application. Please try again.";
    } else {
      title = "Application not found";
      message =
        "The application you're looking for doesn't exist or has been removed.";
    }

    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <XCircle className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">{title}</h2>
              <div className="text-muted-foreground mb-6">{message}</div>
              <div className="flex items-center justify-center gap-2">
                {canRetry && (
                  <Button
                    variant="outline"
                    onClick={handleRetry}
                    disabled={isFetching}
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
                      aria-hidden="true"
                    />
                    {isFetching ? "Retrying…" : "Retry"}
                  </Button>
                )}
                <Button asChild>
                  <Link to="/applications">Back to Applications</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const platformDisplay = app.platform || "iOS";

  // Emit a developer-visible event whenever PPPC is gated by platform on this
  // surface. Helps debug edge cases where an app's platform field is missing
  // or unexpectedly cased ("MacOS" / "OSX") and PPPC silently disappears.
  // The util dedupes by signature so calling this on every render is cheap.
  if (!isMacOs) {
    logPlatformGating({
      feature: "pppc",
      surface: "ApplicationDetailPage",
      reason: "non-macos-platform",
      platform: app.platform ?? null,
      bundleId: app.bundleId ?? null,
      extra: { appId: app.uuid ?? id, appName: app.name },
    });
  } else if (!app.bundleId) {
    logPlatformGating({
      feature: "pppc",
      surface: "ApplicationDetailPage",
      reason: "missing-bundle-id",
      platform: app.platform ?? null,
      bundleId: null,
      extra: { appId: app.uuid ?? id, appName: app.name },
    });
  }

  const handleDelete = () => setShowDeleteDialog(true);

  const confirmDelete = async () => {
    // Hook surfaces success/error toasts; page only navigates.
    await removeApplication(app.uuid || id!);
    navigate("/applications");
  };

  const handleDuplicate = () => toast.success("Application duplicated");
  const handlePublish = () => toast.success("Application published");

  // App configuration list/edit/delete is owned by <AppConfigList /> below.

  return (
    <DetailPageLayout
      backLabel="Back to Applications"
      onBack={() => navigate("/applications")}
      
      actions={[
        { label: "Edit", icon: Edit, onClick: () => navigate(`/applications`) },
        {
          label: (
            <span className="flex items-center justify-between gap-2 w-full">
              <span>Add App Configuration</span>
            </span>
          ),
          icon: Plus,
          onClick: () => setShowAppConfigDialog(true),
        },
        {
          label: (
            <span className="flex items-center justify-between gap-2 w-full">
              <span>
                {isMacOs
                  ? pppcData
                    ? "Edit Privacy Permissions (PPPC)"
                    : "Configure Privacy Permissions (PPPC)"
                  : "Configure Privacy Permissions (PPPC)"}
              </span>
              <Badge
                variant={isMacOs && pppcData ? "secondary" : "outline"}
                className="text-[10px] px-1.5 py-0 font-normal"
              >
                {!isMacOs
                  ? "macOS only"
                  : pppcData
                    ? "Configured"
                    : "Not configured"}
              </Badge>
            </span>
          ),
          icon: isMacOs && pppcData ? Shield : Plus,
          disabled: !isMacOs,
          tooltip: !isMacOs
            ? `Privacy Permissions (PPPC) only apply to macOS apps. This app's platform is ${app.platform || "unknown"}.`
            : undefined,
          onClick: () => {
            if (!isMacOs) return;
            if (!pppcData) {
              setConfirmConfigureKind("pppc");
            } else {
              setShowPppcDialog(true);
            }
          },
        },
        { label: "Duplicate", icon: Copy, onClick: handleDuplicate },
        { label: "Publish", icon: Upload, onClick: handlePublish },
        { label: "Delete", icon: Trash2, onClick: handleDelete, destructive: true },
      ]}
      icon={
        app.iconUrl ? (
          <img src={app.iconUrl} alt={app.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center text-2xl font-bold text-muted-foreground">
            {app.name?.charAt(0)}
          </div>
        )
      }
      title={app.name}
      subtitle={app.bundleId}
      heroExtra={
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
            {app.version && (
              <Badge variant="secondary" className="font-normal">
                v{app.version}
              </Badge>
            )}
            <Badge variant="outline" className="font-normal">
              {app.type || "PUBLIC"}
            </Badge>
            <PlatformIcons platforms={[platformDisplay.toLowerCase() as Platform]} size="sm" />
            {app.size && (
              <>
                <span aria-hidden="true">·</span>
                <span>{formatAppSize(app.size, "Unknown")}</span>
              </>
            )}
            {app.minimumOsVersion && (
              <>
                <span aria-hidden="true">·</span>
                <span>min {platformDisplay} {app.minimumOsVersion}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap min-h-[22px]">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Tags</span>
            <TagsDisplay resourceId={app.uuid || id!} resourceType="application" showRemoveButton />
          </div>
        </div>
      }
      dialogs={
        <>
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Application</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{app.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {bundleIdValidation.valid && (
            <AppConfigDialog
              open={showAppConfigDialog}
              onOpenChange={setShowAppConfigDialog}
              appName={app.name}
              bundleId={bundleIdValidation.normalized}
            />
          )}
          {isMacOs && bundleIdValidation.valid && (
            <PrivacyPermissionsDialog
              open={showPppcDialog}
              onOpenChange={setShowPppcDialog}
              appName={app.name}
              bundleId={bundleIdValidation.normalized}
              existing={pppcData ?? null}
            />
          )}

          <AlertDialog
            open={confirmConfigureKind !== null}
            onOpenChange={(open) => !open && setConfirmConfigureKind(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Create Privacy Permissions policy?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will start a new Privacy Permissions (PPPC) policy for
                  "{app.name}". Make sure one doesn't already exist to avoid
                  duplicates.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setConfirmConfigureKind(null);
                    setShowPppcDialog(true);
                  }}
                >
                  Continue
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column — Overview definition list */}
        <div className="lg:col-span-2">
            <SectionCard title="Overview">
              <dl className="divide-y divide-border/60 text-sm">
                {[
                  { label: "Name", value: <span>{app.name}</span> },
                  { label: "Bundle ID", value: <span>{app.bundleId || "—"}</span> },
                  { label: "Version", value: <span>{app.version || "N/A"}</span> },
                  { label: "Size", value: <span>{formatAppSize(app.size, "Unknown")}</span> },
                  {
                    label: "Platform",
                    value: (
                      <PlatformIcons
                        platforms={[platformDisplay.toLowerCase() as Platform]}
                        size="sm"
                      />
                    ),
                  },
                  {
                    label: "Type",
                    value: (
                      <Badge variant="outline" className="capitalize font-normal">
                        {app.type || "PUBLIC"}
                      </Badge>
                    ),
                  },
                  ...(app.minimumOsVersion
                    ? [{ label: "Minimum OS Version", value: <span>{app.minimumOsVersion}</span> }]
                    : []),
                  ...(app.trackId
                    ? [{ label: "Track ID", value: <span className="font-mono">{app.trackId}</span> }]
                    : []),
                ].map((row) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-[140px_1fr] items-center gap-4 py-2.5 first:pt-0 last:pb-0"
                  >
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="text-foreground">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </SectionCard>
          </div>

        {/* Right Column — slim sidebar */}
        <div className="space-y-6">
            {/* App Configurations — fully managed by AppConfigList. Falls back
                to a missing-bundle-id notice when we can't key off bundleId. */}
            {bundleIdValidation.valid ? (
              <AppConfigList
                bundleId={bundleIdValidation.normalized}
                appName={app.name}
              />
            ) : (
              <SectionCard
                title="App Configurations"
                description="Managed app configuration policies"
              >
                <p className="text-sm text-muted-foreground">
                  No app configurations yet
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  A valid bundle identifier is required to manage app
                  configurations.
                </p>
              </SectionCard>
            )}

            {/* Privacy Permissions (PPPC) — macOS only.
                Hidden entirely for non-macOS apps to keep the sidebar tidy.
                The Actions menu still surfaces a disabled "macOS only" entry
                for discoverability. */}
            {isMacOs && bundleIdValidation.valid && (
              <PrivacyPermissionsCard
                bundleId={bundleIdValidation.normalized}
                onConfigure={() => setConfirmConfigureKind("pppc")}
              />
            )}
            {isMacOs && bundleIdValidation.valid && (
              <SectionCard
                title="Privacy Permissions activity"
                description="Lifecycle events: created, tag changes, deployments, and per-device install confirmations."
              >
                <PppcActivityTimeline
                  scope={{
                    kind: "app",
                    appId: app.uuid ?? id ?? undefined,
                    bundleId: bundleIdValidation.normalized,
                  }}
                />
              </SectionCard>
            )}
            {isMacOs && !app.bundleId && (
              <SectionCard
                title="Privacy Permissions unavailable"
                icon={Info}
                description="Bundle ID is missing — PPPC policies are keyed by bundle identifier."
              >
                <p className="text-xs text-muted-foreground">
                  Edit the application and provide a valid macOS bundle identifier
                  (e.g. <code className="font-mono">com.example.app</code>) to enable
                  Privacy Permissions.
                </p>
              </SectionCard>
            )}
            {isMacOs && app.bundleId && !bundleIdValidation.valid && (
              <SectionCard
                title="Bundle ID looks invalid"
                icon={XCircle}
                variant="destructive"
                description={
                  bundleIdValidation.message ??
                  "Doesn't match reverse-DNS format (e.g. com.example.app)."
                }
                contentClassName="space-y-1.5 text-xs"
              >
                <p>
                  Current value:{" "}
                  <code className="font-mono">{String(app.bundleId ?? "")}</code>
                </p>
                <p className="text-muted-foreground">
                  PPPC will not be loaded until the Bundle ID is valid.
                </p>
              </SectionCard>
            )}
          </div>
        </div>
    </DetailPageLayout>
  );
}
